import React, {useState, useEffect} from 'react'
import {DEFAULT_TEMPLATE_VARIABLES} from './variables'
import {useParams, useNavigate} from 'react-router-dom'
import {useMutation, useQuery} from '@apollo/client'
import {toast} from 'react-toastify'

import {ChannelTabs} from './components/ChannelTabs'
import {VariantEditor} from './components/VariantEditor'
import {VariantPreview} from './components/VariantPreview'
import {
  GET_MESSAGE_TEMPLATE_WITH_VARIANTS,
} from '../../gql/queries/campaigns'
import {
  CREATE_CHANNEL_VARIANT,
  UPDATE_CHANNEL_VARIANT,
  DELETE_CHANNEL_VARIANT,
} from '../../gql/mutations/campaigns'
import {
  CampaignChannel,
  ChannelVariant,
  ChannelVariantInput,
  MessageTemplate,
} from '../../types/campaigns'

const AVAILABLE_CHANNELS: CampaignChannel[] = ['SMS', 'EMAIL', 'WHATSAPP', 'TELEGRAM']

// Sample data for preview
const SAMPLE_VARIABLES = {
  firstName: 'Juan',
  lastName: 'Pérez',
  email: 'juan@example.com',
  phone: '+1234567890',
  dueDate: '2024-01-15',
  amount: '$150.00',
}

export const TemplateEditorPage: React.FC = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()

  const [activeChannel, setActiveChannel] = useState<CampaignChannel>('EMAIL')
  const [currentVariant, setCurrentVariant] = useState<ChannelVariant | null>(null)
  const [showPreview, setShowPreview] = useState(true)
  const [liveBodyText, setLiveBodyText] = useState('')

  // Fetch template with variants
  const {data, loading, refetch} = useQuery(GET_MESSAGE_TEMPLATE_WITH_VARIANTS, {
    variables: {id},
    skip: !id,
  })

  const template: MessageTemplate | null = data?.messageTemplate || null

  // Mutations
  const [createVariant, {loading: creating}] = useMutation(CREATE_CHANNEL_VARIANT)
  const [updateVariant, {loading: updating}] = useMutation(UPDATE_CHANNEL_VARIANT)
  const [deleteVariant, {loading: deleting}] = useMutation(DELETE_CHANNEL_VARIANT)

  // Update current variant when channel changes
  useEffect(() => {
    if (template?.variants) {
      const variant = template.variants.find((v) => v.channel === activeChannel)
      setCurrentVariant(variant || null)
    }
  }, [activeChannel, template])

  const handleSaveVariant = async (variantData: ChannelVariantInput) => {
    try {
      if (currentVariant) {
        // Update existing variant
        await updateVariant({
          variables: {
            id: currentVariant.id,
            ...variantData,
          },
        })
        toast.success('✅ Variante actualizada correctamente')
      } else {
        // Create new variant
        await createVariant({
          variables: {
            templateId: id,
            variantInput: variantData,
          },
        })
        toast.success('✅ Variante creada correctamente')
      }
      refetch()
    } catch (error: any) {
      toast.error(error.message || 'Error al guardar variante')
    }
  }

  const handleDeleteVariant = async () => {
    if (!currentVariant) return

    if (!window.confirm('¿Estás seguro de eliminar esta variante?')) return

    try {
      await deleteVariant({
        variables: {id: currentVariant.id},
      })
      toast.success('✅ Variante eliminada correctamente')
      refetch()
    } catch (error: any) {
      toast.error(error.message || 'Error al eliminar variante')
    }
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{minHeight: '400px'}}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Cargando...</span>
        </div>
      </div>
    )
  }

  if (!template) {
    return (
      <div className='alert alert-danger'>
        Template no encontrado. <a href='/templates'>Volver a la lista</a>
      </div>
    )
  }

  return (
    <div className='container-fluid'>
      {/* Header */}
      <div className='d-flex justify-content-between align-items-center mb-5'>
        <div>
          <h1 className='mb-2'>📝 Editar Template: {template.name}</h1>
          <p className='text-muted mb-0'>{template.description}</p>
        </div>
        <div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className='btn btn-light me-2'
          >
            {showPreview ? '📝 Solo Editor' : '👁️ Mostrar Preview'}
          </button>
          <button onClick={() => navigate('/templates')} className='btn btn-secondary'>
            ← Volver
          </button>
        </div>
      </div>

      {/* Template Info */}
      <div className='card mb-5 shadow-sm'>
        <div className='card-body'>
          <div className='row'>
            <div className='col-md-3'>
              <small className='text-muted'>Tipo</small>
              <div className='fw-bold'>{template.templateType}</div>
            </div>
            <div className='col-md-3'>
              <small className='text-muted'>Canales Disponibles</small>
              <div className='fw-bold'>
                {template.allChannels.length > 0
                  ? template.allChannels.join(', ')
                  : 'Ninguno (crea variantes abajo)'}
              </div>
            </div>
            <div className='col-md-3'>
              <small className='text-muted'>Variables</small>
              <div className='fw-bold'>
                {template.variables && template.variables.length > 0
                  ? (Array.isArray(template.variables) 
                      ? template.variables.map((v) => `{{${v}}}`).join(', ')
                      : template.variables)
                  : 'Ninguna'}
              </div>
            </div>
            <div className='col-md-3'>
              <small className='text-muted'>Variantes Creadas</small>
              <div className='fw-bold'>{template.variants?.length || 0} / {AVAILABLE_CHANNELS.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Channel Tabs */}
      <ChannelTabs
        channels={AVAILABLE_CHANNELS}
        activeChannel={activeChannel}
        onChannelChange={setActiveChannel}
        variants={template.variants || []}
      />

      {/* Main Content: Editor + Preview */}
      <div className='row'>
        {/* Editor Column */}
        <div className={showPreview ? 'col-lg-6' : 'col-12'}>
          <VariantEditor
            channel={activeChannel}
            variant={currentVariant}
            onSave={handleSaveVariant}
            onDelete={currentVariant ? handleDeleteVariant : undefined}
            templateVariables={
              Array.isArray(template.variables) && template.variables.length > 0
                ? template.variables
                : DEFAULT_TEMPLATE_VARIABLES
            }
            onBodyTextChange={setLiveBodyText}
          />
        </div>

        {/* Preview Column */}
        {showPreview && (
          <div className='col-lg-6'>
            {currentVariant ? (
              <VariantPreview 
                variant={currentVariant} 
                variables={SAMPLE_VARIABLES}
                liveBodyText={liveBodyText}
              />
            ) : (
              <div className='card'>
                <div className='card-body text-center py-5'>
                  <div className='fs-1 mb-3'>👈</div>
                  <h4 className='text-muted'>Sin Variante</h4>
                  <p className='text-muted'>
                    Crea una variante para el canal {activeChannel} para ver el preview
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Loading overlay */}
      {(creating || updating || deleting) && (
        <div
          className='position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center'
          style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999}}
        >
          <div className='card shadow'>
            <div className='card-body text-center p-5'>
              <div className='spinner-border text-primary mb-3' role='status'>
                <span className='visually-hidden'>Guardando...</span>
              </div>
              <div>Guardando cambios...</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
